import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { AggregatedStat, Campaign, FacebookCampaignInsight } from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import { StatsCardV1 } from '@feathr/components';

import {
  ClicksStat,
  ConversionsStat,
  NewUsersStat,
  ReachableNewAudienceStat,
  ReachStat,
  UniqueClicksStat,
  ViewsStat,
} from '../Stats';

interface ICardProps {
  aggregatedStats: AggregatedStat;
  campaign?: Campaign;
  clicksLabel?: string;
  clicksTooltip?: string;
  conversions?: number;
  facebookCampaignStats?: FacebookCampaignInsight;
  isMonetization?: boolean;
  isLoadingReach?: boolean;
  reachLabel?: string;
  reachTooltipText?: string;
  targetImpressions?: number;
  totalUsers?: number;
  uniqueClicksLabel?: string;
  uniqueClicksTooltipText?: string;
  viewsLabel?: string;
  viewsTooltip?: string;
  isEventOrFlight?: boolean;
  reachedUsers?: number;
}

function ActivitySummaryCard({
  campaign,
  clicksLabel,
  clicksTooltip,
  conversions,
  aggregatedStats,
  facebookCampaignStats,
  isMonetization,
  isLoadingReach,
  reachLabel,
  reachTooltipText,
  targetImpressions,
  totalUsers,
  uniqueClicksLabel,
  uniqueClicksTooltipText,
  viewsLabel,
  viewsTooltip,
  isEventOrFlight,
  reachedUsers,
}: ICardProps): JSX.Element | null {
  // Email campaigns now use EmailActivitySummaryCard
  if (campaign?.isEmail) {
    return null;
  }

  const clicks = aggregatedStats.get('num_clicks', 0);
  const newUsers = aggregatedStats.get('num_new_users_active', 0);
  const views = aggregatedStats.get('num_views', 0);
  let users = 0;
  let uniqueClicks = 0;
  if (campaign?.isFacebook && facebookCampaignStats) {
    users = facebookCampaignStats.get('reach');
    uniqueClicks = facebookCampaignStats.get('unique_clicks');
  } else if (campaign?.isOmniCampaign) {
    users = reachedUsers || aggregatedStats.get('num_users_active', 0);
    uniqueClicks = aggregatedStats.get('num_clicks_active', 0);
  } else {
    users = aggregatedStats.get('num_users_active', 0);
    uniqueClicks = aggregatedStats.get('num_clicks_active', 0);
  }

  const reachableNewAudience = aggregatedStats.get('num_new_users_active_reachable', 0);

  return (
    <StatsCardV1 title={reportModuleLabels.includeActivitySummary}>
      <>
        {!campaign?.isGoogle && (
          <ReachStat
            isLoading={isLoadingReach}
            isMonetization={isMonetization}
            label={reachLabel}
            tooltip={reachTooltipText}
            totalUsers={totalUsers}
            value={users}
          />
        )}
        {!isEventOrFlight && (
          <ViewsStat
            conversions={conversions}
            isMonetization={isMonetization}
            label={viewsLabel}
            targetImpressions={targetImpressions}
            tooltip={viewsTooltip}
            users={users}
            value={views}
          />
        )}
        {!campaign?.isGoogle && (
          <UniqueClicksStat
            label={uniqueClicksLabel}
            tooltip={uniqueClicksTooltipText}
            value={uniqueClicks}
          />
        )}
        {!isEventOrFlight && (
          <ClicksStat
            label={clicksLabel}
            tooltip={clicksTooltip}
            type={campaign?.get('_cls')}
            value={clicks}
            views={views}
          />
        )}
        {campaign?.isGoogle && (
          <ConversionsStat goals={campaign.get('goals', [])} value={conversions ?? 0} />
        )}
        {newUsers > 0 && (
          <NewUsersStat
            tooltip={'The combined audience from all audience expansion campaigns in the Flight.'}
            value={newUsers}
          />
        )}
        {campaign?.isAwarenessCampaign && (
          <ReachableNewAudienceStat
            isMonetization={isMonetization}
            totalUsers={totalUsers}
            value={reachableNewAudience}
          />
        )}
      </>
    </StatsCardV1>
  );
}

export default observer(ActivitySummaryCard);
